import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, Box, AppBar, Toolbar, IconButton, Button, useMediaQuery, CircularProgress } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { SnackbarProvider, useSnackbar } from 'notistack';
import DeviceList from './components/DeviceList';
import DeviceDetails from './components/DeviceDetails';
import DeviceCommandManager from './components/DeviceCommandManager';
// PublicWifiManager removed – GroupsManager now serves as the Manager page under Public WiFi
import PublicWifiOverview from './components/PublicWifiOverview';
import PublicWifiExpansion from './components/PublicWifiExpansion';
import BulkConfiguration from './components/BulkConfiguration';
import DefaultConfig from './components/DefaultConfig';
import GroupsManager from './components/GroupsManager';
import AlertsPage from './components/AlertsPage';
import Login from './components/Login';
import RttyPage from './pages/RttyPage';
import NetworkStatusPage from './pages/NetworkStatusPage';
import InstallationStatusTab from './components/InstallationStatusTab';
import TreeOptimizationView from './components/treeOptimization/TreeOptimizationView';
import DeviceNeighborsView from './components/DeviceNeighborsView';
import DeviceTmCandidatesView from './components/DeviceTmCandidatesView';
import Sidebar from './components/Sidebar';
import RequireRole from './components/RequireRole';
import { lightTheme, darkTheme } from './theme';
import { auth } from './services/auth';
import {
  ROLE_BACKHAUL_READ,
  ROLE_BACKHAUL_WRITE,
} from './services/roles';
import { api } from './services/api';
import config from './config';
import './App.css';

const DEVICE_CACHE_TTL_MS = 60 * 1000;

const normalizeFields = (fieldsArray = []) => {
  const unique = new Set();
  fieldsArray.forEach((field) => {
    if (!field && field !== 0) return;
    const normalized = field.toString().trim().toLowerCase();
    if (normalized) {
      unique.add(normalized);
    }
  });
  return Array.from(unique).sort();
};

const snapshotOptions = (options) => ({
  ...options,
  fields: Array.isArray(options.fields) ? [...options.fields] : undefined,
  compatible: Array.isArray(options.compatible) ? [...options.compatible] : undefined,
});

const normalizeCompat = (values = []) => {
  const unique = new Set();
  values.forEach((value) => {
    if (!value && value !== 0) return;
    const normalized = value.toString().trim();
    if (normalized) {
      unique.add(normalized);
    }
  });
  return Array.from(unique);
};

// Include all device types so parent-child relationships work across types
// Nvidia devices (EdgeCore children) don't have a 'compatible' field - they're identified by capabilities.model
const COMPAT_NETWORK = []; // Empty = no filter, fetch all devices

const getDefaultDeviceFetchOptions = () => {
  // Always include wifi_interfaces for EdgeCore children matching and map view
  return {
    fields: normalizeFields(['core', 'radios', 'wifi_interfaces', 'configuration']),
    compatible: normalizeCompat(COMPAT_NETWORK),
  };
};

const DEFAULT_DEVICE_FETCH_OPTIONS = getDefaultDeviceFetchOptions();

// Map pathname to tab ID
const getTabIdFromPath = (pathname) => {
  if (pathname === '/overview') return 8;
  if (pathname === '/devices') return 0;
  if (pathname === '/alerts') return 1;
  if (pathname === '/commands') return 2;
  if (pathname === '/analysis') return 3;
  if (pathname === '/neighbors') return 3;
  if (pathname === '/public-wifi') return 5;
  if (pathname === '/configuration') return 6;
  if (pathname === '/default-config') return 7;
  if (pathname === '/public-wifi-overview') return 9;
  if (pathname === '/public-wifi-expansion') return 10;
  if (pathname === '/groups-manager') return 5;
  // Default to Overview if path doesn't match
  return 8;
};

const isPathAllowedForSession = (pathname) => {
  if (pathname.startsWith('/rtty/')) {
    return auth.canWriteBackhaul();
  }
  if (pathname.startsWith('/neighbors/') || pathname.startsWith('/tm-candidates/')) {
    return auth.canReadBackhaul();
  }
  if (pathname === '/analysis' || pathname === '/neighbors') {
    return auth.canReadBackhaul();
  }
  if (pathname === '/commands') {
    return auth.canWriteBackhaul();
  }
  if (pathname === '/configuration' || pathname === '/default-config') {
    return auth.canWriteDeviceConfig();
  }
  if (pathname === '/public-wifi' || pathname === '/public-wifi-overview' || pathname === '/public-wifi-expansion' || pathname === '/groups-manager') {
    return auth.canReadPublicWifi();
  }
  return true;
};

// Main content component that handles tabs and device list
function MainContent({ devices, onDevicesUpdate, onDeviceSelect, selectedDevice, onRefresh, devicesLoaded, onLogout, isDarkMode, onThemeToggle, deviceLoadError, onDismissDeviceLoadError }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // Initialize activeTab from current route, preserving user's current location
  const [activeTab, setActiveTab] = useState(() => getTabIdFromPath(location.pathname));
  const [refreshing, setRefreshing] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [environment, setEnvironment] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  // Group highlighting – serials to highlight on the Devices map
  const [highlightedGroupSerials, setHighlightedGroupSerials] = useState([]);
  
  // Check if mobile
  const isMobile = useMediaQuery('(max-width:900px)');
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  const handleRefreshDevices = useCallback(
    async (options) => {
      if (!onRefresh) return;
      try {
        setRefreshing(true);
        await onRefresh(options);
        // A successful manual refresh clears any sticky load error
        // banner from the auto-load path so we don't keep nagging
        // the operator about a problem that's now resolved.
        if (typeof onDismissDeviceLoadError === 'function') {
          onDismissDeviceLoadError();
        }
      } catch (error) {
        console.error('Error refreshing devices:', error);
        enqueueSnackbar(error?.message || 'Failed to refresh devices', { variant: 'error' });
      } finally {
        setRefreshing(false);
      }
    },
    [onRefresh, enqueueSnackbar, onDismissDeviceLoadError]
  );

  // Surface the App-level auto-load failure (caught at the only call
  // site that doesn't otherwise route through handleRefreshDevices).
  // We use a persistent snackbar with an explicit Retry action so the
  // operator can recover without hunting for the toolbar refresh
  // button — and Dismiss for the case where they just want to keep
  // working with whatever cache is on screen.
  useEffect(() => {
    if (!deviceLoadError) return;
    const message = deviceLoadError.isNetworkError || deviceLoadError.isTimeout
      ? `${deviceLoadError.message || 'Failed to load devices.'} The backend may still be initializing.`
      : (deviceLoadError.message || 'Failed to load devices.');
    enqueueSnackbar(message, {
      variant: deviceLoadError.statusCode === 503 ? 'warning' : 'error',
      persist: true,
      preventDuplicate: false,
      key: `device-load-error-${deviceLoadError.nonce}`,
      action: (snackbarKey) => (
        <>
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              if (typeof onDismissDeviceLoadError === 'function') {
                onDismissDeviceLoadError();
              }
              handleRefreshDevices({ force: true });
            }}
          >
            Retry
          </Button>
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              if (typeof onDismissDeviceLoadError === 'function') {
                onDismissDeviceLoadError();
              }
            }}
          >
            Dismiss
          </Button>
        </>
      ),
    });
  // handleRefreshDevices is intentionally referenced via closure: it
  // only changes when onRefresh / enqueueSnackbar identities change,
  // both of which are stable — listing it here would re-fire the
  // snackbar on every parent re-render that bumps onRefresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceLoadError, enqueueSnackbar]);

  // Sync activeTab with current route (preserves tab on refresh)
  // Router is the single source of truth - always derive tab from pathname
  useEffect(() => {
    const tabId = getTabIdFromPath(location.pathname);
    setActiveTab(tabId);
  }, [location.pathname]); // Only depend on pathname - router is source of truth

  // Get user info and environment on mount
  useEffect(() => {
    const info = auth.getUserInfo();
    setUserInfo(info);
    setEnvironment(config.getEnvironment());
  }, []);

  useEffect(() => {
    if (!isPathAllowedForSession(location.pathname)) {
      enqueueSnackbar('You do not have access to that page.', { variant: 'warning' });
      navigate('/overview', { replace: true });
    }
  }, [location.pathname, enqueueSnackbar, navigate]);

  // Listen for unauthorized events
  useEffect(() => {
    const handleUnauthorized = (event) => {
      enqueueSnackbar(event.detail.message, { 
        variant: 'error',
        autoHideDuration: 5000
      });
      onLogout();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [enqueueSnackbar, onLogout]);

  // Listen for tab switch events (from InstallationStatusTab)
  useEffect(() => {
    const handleSwitchToTab = (event) => {
      const tabIndex = event.detail?.tabIndex;
      if (typeof tabIndex === 'number') {
        setActiveTab(tabIndex);
      }
    };

    window.addEventListener('switchToTab', handleSwitchToTab);
    return () => window.removeEventListener('switchToTab', handleSwitchToTab);
  }, []);

  // Clear selected device when tab changes
  useEffect(() => {
    if (selectedDevice) {
      onDeviceSelect(null);
    }
  }, [activeTab, onDeviceSelect, selectedDevice]);

  // Removed auto-refresh for empty devices as it can cause infinite retry loops
  // User can manually refresh if needed via the refresh button

  const handleDeviceClick = (device) => {
    onDeviceSelect(device);
    navigate(`/device/${device.cap_serial}`);
  };

  // Highlight group devices on the map: switch to Devices tab and mark serials
  const handleHighlightGroupOnMap = (serials) => {
    setHighlightedGroupSerials(Array.isArray(serials) ? serials : []);
    setActiveTab(0);
    navigate('/devices');
  };

  return (
    <Box sx={{ display: 'flex', width: '100vw', minHeight: '100vh' }}>
      <Sidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onThemeToggle={onThemeToggle}
        isDarkMode={isDarkMode}
        onLogout={onLogout}
        userInfo={userInfo}
        environment={environment}
        mobileOpen={mobileOpen}
        onMobileClose={handleMobileClose}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: 'auto',
          minHeight: '100vh',
          bgcolor: 'background.default',
          overflowX: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Mobile AppBar with Hamburger Menu */}
        {isMobile && (
          <AppBar
            position="fixed"
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 1,
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: 1,
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#000',
                    fontWeight: 900,
                    fontSize: '1.2rem',
                    mr: 1,
                    verticalAlign: 'middle',
                  }}
                >
                  J
                </Box>
                <Box component="span" sx={{ fontWeight: 700, fontSize: '1rem', verticalAlign: 'middle' }}>
                  NMS
                </Box>
              </Box>
            </Toolbar>
          </AppBar>
        )}
        
        <Box sx={{ 
          width: '100%', 
          p: { xs: 1, sm: 2, md: 3 },
          mt: { xs: isMobile ? 7 : 0, md: 0 },
          flexGrow: 1,
        }}>
          <Box role="tabpanel" hidden={activeTab !== 8} id="tabpanel-8" aria-labelledby="tab-8">
            <InstallationStatusTab isActive={activeTab === 8} devices={devices || []} />
          </Box>

          <Box role="tabpanel" hidden={activeTab !== 0} id="tabpanel-0" aria-labelledby="tab-0">
            <DeviceList 
              devices={devices} 
              onDevicesUpdate={onDevicesUpdate} 
              onDeviceSelect={handleDeviceClick} 
              selectedDevice={selectedDevice}
              onRefresh={handleRefreshDevices}
              devicesLoaded={devicesLoaded && !refreshing}
              highlightedGroupSerials={highlightedGroupSerials}
            />
          </Box>

          <Box role="tabpanel" hidden={activeTab !== 1} id="tabpanel-1" aria-labelledby="tab-1">
            <AlertsPage isActive={activeTab === 1} />
          </Box>

          {auth.canWriteBackhaul() && (
          <Box role="tabpanel" hidden={activeTab !== 2} id="tabpanel-2" aria-labelledby="tab-2">
            <DeviceCommandManager 
              devices={devices} 
              onCommandSent={() => {}} 
            />
          </Box>
          )}

          {auth.canReadBackhaul() && (
          <Box role="tabpanel" hidden={activeTab !== 3} id="tabpanel-3" aria-labelledby="tab-3">
            <TreeOptimizationView devices={devices} />
          </Box>
          )}

          <Box role="tabpanel" hidden={activeTab !== 5} id="tabpanel-5" aria-labelledby="tab-5">
            <GroupsManager
              devices={devices || []}
              onHighlightGroupOnMap={handleHighlightGroupOnMap}
              isActive={activeTab === 5}
              readOnly={auth.isReadOnly()}
            />
          </Box>

          <Box role="tabpanel" hidden={activeTab !== 9} id="tabpanel-9" aria-labelledby="tab-9">
            <PublicWifiOverview
              devices={devices}
              isActive={activeTab === 9}
              onRefresh={handleRefreshDevices}
              readOnly={auth.isReadOnly()}
            />
          </Box>

          <Box role="tabpanel" hidden={activeTab !== 10} id="tabpanel-10" aria-labelledby="tab-10">
            <PublicWifiExpansion
              devices={devices}
              isActive={activeTab === 10}
              readOnly={auth.isReadOnly()}
            />
          </Box>

          {auth.canWriteDeviceConfig() && (
          <Box role="tabpanel" hidden={activeTab !== 6} id="tabpanel-6" aria-labelledby="tab-6">
            <BulkConfiguration 
              devices={devices}
              onDevicesUpdate={onDevicesUpdate}
            />
          </Box>
          )}

          {auth.canWriteDeviceConfig() && (
          <Box role="tabpanel" hidden={activeTab !== 7} id="tabpanel-7" aria-labelledby="tab-7">
            <DefaultConfig />
          </Box>
          )}

        </Box>
      </Box>
    </Box>
  );
}

function App() {
  const [devices, setDevices] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(() => auth.isAuthenticated());
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  // deviceLoadError carries the most recent failure from the initial
  // /devices fetch (or its retry). It's surfaced to the operator via
  // a snackbar inside MainContent — keeping the state up here lets us
  // catch the rejection at the only call site that doesn't otherwise
  // handle it (the auth-gated useEffect below) without leaking an
  // unhandled promise into the React error boundary, which is what
  // was producing the "Uncaught runtime error: Network error while
  // loading devices" overlay when the proxy stalled or cold-started
  // the backend. Each entry has a stable `nonce` so MainContent's
  // useEffect re-fires the snackbar on every fresh failure even when
  // the message text repeats.
  const [deviceLoadError, setDeviceLoadError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });
  const devicesCacheRef = useRef({
    timestamp: 0,
    data: null,
    optionsKey: JSON.stringify(snapshotOptions(DEFAULT_DEVICE_FETCH_OPTIONS)),
  });
  const pendingDevicesRequestRef = useRef(null);
  const deviceFetchOptionsRef = useRef(snapshotOptions(DEFAULT_DEVICE_FETCH_OPTIONS));
  const noDevicesForProjectRef = useRef(false);

  // Global unauthorized event handler
  useEffect(() => {
    const handleUnauthorized = (event) => {
      console.warn('Session expired, redirecting to login');
      setIsAuthenticated(false);
      setDevices([]);
      setSelectedDevice(null);
      setDevicesLoaded(false);
      // Redirect to login/root
      window.location.assign('/');
    };

    const handleExpiringSoon = (event) => {
      console.warn('Session expiring soon:', event.detail.message);
      // You could show a notification here if you want
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:expiring-soon', handleExpiringSoon);
    
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:expiring-soon', handleExpiringSoon);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!auth.isAuthenticated()) {
        if (!cancelled) {
          setIsAuthenticated(false);
          setSessionLoading(false);
        }
        return;
      }
      setSessionLoading(true);
      try {
        await auth.fetchSession();
        if (!cancelled) {
          setIsAuthenticated(true);
        }
      } catch (err) {
        console.error('Failed to load auth session:', err);
        if (!cancelled) {
          auth.logout();
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) {
          setSessionLoading(false);
        }
      }
    };

    bootstrap();

    const tokenCheckInterval = setInterval(() => {
      if (isAuthenticated && !auth.isAuthenticated()) {
        setIsAuthenticated(false);
        setDevices([]);
        setSelectedDevice(null);
      }
    }, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(tokenCheckInterval);
    };
  }, [isAuthenticated]);

  const refreshDevices = useCallback(
    async ({
      force = false,
      fields,
      addFields,
      compatible,
    } = {}) => {
      if (!isAuthenticated) {
        return;
      }

      const mergedOptions = {
        ...deviceFetchOptionsRef.current,
        fields: normalizeFields(deviceFetchOptionsRef.current.fields || DEFAULT_DEVICE_FETCH_OPTIONS.fields || []),
        compatible: normalizeCompat(deviceFetchOptionsRef.current.compatible || DEFAULT_DEVICE_FETCH_OPTIONS.compatible || []),
      };

      if (Array.isArray(addFields) && addFields.length > 0) {
        mergedOptions.fields = normalizeFields([...(mergedOptions.fields || []), ...addFields]);
      }

      if (Array.isArray(fields) && fields.length > 0) {
        mergedOptions.fields = normalizeFields(fields);
      }

      if (Array.isArray(compatible) && compatible.length > 0) {
        mergedOptions.compatible = normalizeCompat(compatible);
      }

      const normalizedOptions = snapshotOptions(mergedOptions);
      const optionsKey = JSON.stringify(normalizedOptions);
      const now = Date.now();
      const cache = devicesCacheRef.current;

      if (
        !force &&
        cache.data &&
        cache.optionsKey === optionsKey &&
        now - cache.timestamp < DEVICE_CACHE_TTL_MS
      ) {
        setDevices(cache.data);
        setDevicesLoaded(true);
        deviceFetchOptionsRef.current = normalizedOptions;
        return cache.data;
      }

      if (
        !force &&
        pendingDevicesRequestRef.current &&
        pendingDevicesRequestRef.current.optionsKey === optionsKey
      ) {
        return pendingDevicesRequestRef.current.promise;
      }

      const fetchPromise = (async () => {
        try {
          const response = await api.getDevices(normalizedOptions);
          // Handle new response format with metadata
          const devicesArray = response?.devices || (Array.isArray(response) ? response : []);
          const noDevicesForProject = response?.noDevicesForProject || false;
          
          devicesCacheRef.current = { data: devicesArray, timestamp: Date.now(), optionsKey };
          deviceFetchOptionsRef.current = normalizedOptions;
          setDevices(devicesArray);
          
          // Set flag if we got explicit "no devices for project" message
          if (noDevicesForProject) {
            noDevicesForProjectRef.current = true;
          }
          
          return devicesArray;
        } catch (error) {
          console.error('Error fetching devices:', error);
          if (error.message === 'Session expired') {
            return;
          }
          // Don't retry on 503 errors
          if (error.statusCode === 503 || error.noRetry) {
            noDevicesForProjectRef.current = true;
          }
          setDevices([]);
          throw error;
        } finally {
          setDevicesLoaded(true);
          if (
            pendingDevicesRequestRef.current &&
            pendingDevicesRequestRef.current.promise === fetchPromise
          ) {
            pendingDevicesRequestRef.current = null;
          }
        }
      })();

      pendingDevicesRequestRef.current = { promise: fetchPromise, optionsKey };
      return fetchPromise;
    },
    [isAuthenticated]
  );

  useEffect(() => {
    // Fetch devices only once when authenticated and not on device details route
    if (isAuthenticated) {
      const path = window.location?.pathname || '';
      if (path.startsWith('/device/')) {
        // Don't fetch all devices on device details page - device data already has children info
        setDevicesLoaded(true);
        return;
      }

      // refreshDevices() rejects with a typed Error on transient
      // failures (network drop, proxy timeout, AbortError after the
      // 60 s ceiling, 503 cold-start). We MUST catch here — without
      // this the rejection bubbles up to the React error boundary
      // and the operator sees an "Uncaught runtime error" overlay,
      // which was the symptom under heavy backend load / cold start.
      //
      // Strategy: try once, and if the failure looks transient
      // (network/timeout — the original handler in api.js tags both
      // with `isNetworkError`/`isTimeout`), wait 5 s and try one more
      // time. The "wait a moment and try again" copy already in the
      // network-error message implies the original author wanted this
      // behaviour — we now actually do it. Anything still failing
      // after the retry is surfaced via the snackbar in MainContent
      // and the user can click the manual refresh button to retry.
      let cancelled = false;
      let retryTimer = null;

      // 503 is intentionally NOT retried — handleResponse tags it
      // with noRetry because the backend uses 503 to mean "no
      // devices connected for this project", which is a legitimate
      // empty state, not a transient failure. We only auto-retry
      // genuine transport-level problems.
      const isTransient = (err) => Boolean(err && (err.isNetworkError || err.isTimeout) && !err.noRetry);

      const attempt = async (remaining) => {
        try {
          await refreshDevices({ force: true });
          if (!cancelled) {
            setDeviceLoadError(null);
          }
        } catch (err) {
          if (cancelled) return;
          if (err?.message === 'Session expired') {
            // The auth handler already re-routed us — nothing to do.
            return;
          }
          if (remaining > 0 && isTransient(err)) {
            retryTimer = setTimeout(() => {
              retryTimer = null;
              attempt(remaining - 1);
            }, 5000);
            return;
          }
          // 503 means "no devices connected for this project" — not
          // a failure mode worth nagging the operator about; the
          // empty-list UI in DeviceList already explains it.
          if (err?.statusCode === 503 || err?.noRetry) {
            return;
          }
          setDeviceLoadError({
            message: err?.message || 'Failed to load devices.',
            isNetworkError: Boolean(err?.isNetworkError),
            isTimeout: Boolean(err?.isTimeout),
            statusCode: err?.statusCode,
            // nonce makes a repeated identical-message failure still
            // re-trigger MainContent's snackbar effect.
            nonce: Date.now(),
          });
        }
      };

      attempt(1);
      return () => {
        cancelled = true;
        if (retryTimer) {
          clearTimeout(retryTimer);
          retryTimer = null;
        }
      };
    }

    setDevicesLoaded(false);
    setDeviceLoadError(null);
    deviceFetchOptionsRef.current = snapshotOptions(getDefaultDeviceFetchOptions());
    devicesCacheRef.current = {
      data: null,
      timestamp: 0,
      optionsKey: JSON.stringify(deviceFetchOptionsRef.current),
    };
    return undefined;
  }, [isAuthenticated, refreshDevices]);

  const handleLoginSuccess = async () => {
    setSessionLoading(true);
    try {
      await auth.fetchSession();
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Failed to load auth session after login:', err);
      auth.logout();
      setIsAuthenticated(false);
      return;
    } finally {
      setSessionLoading(false);
    }
    setDevicesLoaded(false);
    deviceFetchOptionsRef.current = snapshotOptions(getDefaultDeviceFetchOptions());
    devicesCacheRef.current = {
      data: null,
      timestamp: 0,
      optionsKey: JSON.stringify(deviceFetchOptionsRef.current),
    };
  };

  const handleLogout = () => {
    auth.logout();
    setIsAuthenticated(false);
    setDevices([]);
    setSelectedDevice(null);
    setDevicesLoaded(false);
    deviceFetchOptionsRef.current = snapshotOptions(getDefaultDeviceFetchOptions());
    devicesCacheRef.current = {
      data: null,
      timestamp: 0,
      optionsKey: JSON.stringify(deviceFetchOptionsRef.current),
    };
    // Redirect to login/root
    window.location.assign('/');
  };

  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
  };

  const handleDevicesUpdate = (updatedDevices) => {
    if (updatedDevices === null) {
      setDevices(null);
      devicesCacheRef.current = {
        data: null,
        timestamp: 0,
        optionsKey: JSON.stringify(deviceFetchOptionsRef.current),
      };
      return;
    }
    const normalized = Array.isArray(updatedDevices) ? updatedDevices : [];
    setDevices(normalized);
    devicesCacheRef.current = {
      data: normalized,
      timestamp: Date.now(),
      optionsKey: JSON.stringify(deviceFetchOptionsRef.current),
    };
    setDevicesLoaded(true);
  };

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <Login onLoginSuccess={handleLoginSuccess} />
      </ThemeProvider>
    );
  }

  if (sessionLoading) {
    return (
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <Router>
          <Routes>
            <Route 
              path="/device/:serial" 
              element={<DeviceDetails devices={devices || []} onLogout={handleLogout} canWriteBackhaul={auth.canWriteBackhaul()} />} 
            />
            <Route 
              path="/rtty/:serial" 
              element={(
                <RequireRole roles={ROLE_BACKHAUL_WRITE}>
                  <RttyPage />
                </RequireRole>
              )} 
            />
            <Route 
              path="/neighbors/:serial" 
              element={(
                <RequireRole roles={ROLE_BACKHAUL_READ}>
                  <DeviceNeighborsView devices={devices || []} />
                </RequireRole>
              )} 
            />
            <Route 
              path="/tm-candidates/:serial" 
              element={(
                <RequireRole roles={ROLE_BACKHAUL_READ}>
                  <DeviceTmCandidatesView />
                </RequireRole>
              )} 
            />
            <Route 
              path="/project/installation-status" 
              element={<NetworkStatusPage />} 
            />
            <Route 
              path="/" 
              element={<Navigate to="/overview" replace />} 
            />
            <Route 
              path="*" 
              element={
                <MainContent 
                  devices={devices}
                  devicesLoaded={devicesLoaded}
                  onDevicesUpdate={handleDevicesUpdate}
                  onDeviceSelect={handleDeviceSelect}
                  selectedDevice={selectedDevice}
                  onRefresh={refreshDevices}
                  onLogout={handleLogout}
                  isDarkMode={isDarkMode}
                  onThemeToggle={handleThemeToggle}
                  deviceLoadError={deviceLoadError}
                  onDismissDeviceLoadError={() => setDeviceLoadError(null)}
                />
              } 
            />
          </Routes>
        </Router>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App; 